import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global axios defaults
axios.defaults.timeout = 15000;

// Axios interceptor: auto-refresh token on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes("/auth/login") && !originalRequest.url?.includes("/auth/refresh")) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axios(originalRequest);
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          const res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, { refresh_token: refreshToken }, { withCredentials: true });
          const newToken = res.data.token;
          localStorage.setItem("token", newToken);
          processQueue(null, newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
      } finally {
        isRefreshing = false;
      }
    }
    if (error.code === "ECONNABORTED") console.warn("Request timeout");
    else if (!error.response) console.warn("Network error");
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers, withCredentials: true });
      setUser(response.data);
    } catch {
      // Try refresh
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, { refresh_token: refreshToken }, { withCredentials: true });
          localStorage.setItem("token", res.data.token);
          const me = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${res.data.token}` } });
          setUser(me.data);
        } catch {
          localStorage.removeItem("token");
          localStorage.removeItem("refresh_token");
          setUser(false);
        }
      } else {
        localStorage.removeItem("token");
        setUser(false);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password }, { withCredentials: true });
    if (response.data.token) localStorage.setItem("token", response.data.token);
    if (response.data.refresh_token) localStorage.setItem("refresh_token", response.data.refresh_token);
    setUser(response.data);
    return response.data;
  };

  const register = async (name, email, phone, password, birth_date = "", referral_code = "") => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, { name, email, phone, password, birth_date, referral_code }, { withCredentials: true });
    if (response.data.token) localStorage.setItem("token", response.data.token);
    if (response.data.refresh_token) localStorage.setItem("refresh_token", response.data.refresh_token);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    setUser(false);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, getAuthHeaders, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
